import { typedArrayToBuffer } from '@iotize/common/byte-converter';
import {
  KaitaiStreamReader,
  KaitaiStreamWriter,
  StreamBufferType,
} from '@iotize/common/byte-stream';

import { debug } from '../debug';
import { CRC } from '../error-detection/crc';

export function enumKeyOrValueToNumber<T>(
  key: string | number | T,
  mapping: any,
): number {
  if (typeof key === 'string') {
    // if (!(key in mapping)) {
    //     throw new Error(``)
    // }
    return mapping[key];
  } else {
    return key as any as number;
  }
}

interface ReadSringOptions {
  terminator?: string | number;
}
export class TapStreamReader extends KaitaiStreamReader {
  static fromArray(array: Uint8Array): TapStreamReader {
    const buffer = typedArrayToBuffer(array);
    return new TapStreamReader(buffer);
  }

  static create(packet: Uint8Array | TapStreamReader): TapStreamReader {
    return packet instanceof Uint8Array
      ? TapStreamReader.fromArray(packet)
      : packet;
  }

  readStr(n: number): string {
    return String.fromCharCode.apply(
      null,
      this.readBytes(n) as any as number[],
    );
  }

  /**
   * Read given byte size as string
   * @param n number of byte to read
   */
  readString(n: number = this.byteLeft) {
    return this.readStr(n);
  }

  /**
   * Read string until terminator character is reached
   */
  readStringTerminator(terminator: string) {
    let result = '';
    while (true) {
      const lastByte = this.readU1();
      const lastChar = String.fromCharCode(lastByte);
      if (lastChar !== terminator) {
        result += lastChar;
      } else {
        break;
      }
    }
    return result;
  }

  readU2(): number {
    return this._isBigEndian ? this.readU2be() : this.readU2le();
  }

  readU4(): number {
    return this._isBigEndian ? this.readU4be() : this.readU4le();
  }

  readF4(): number {
    return this._isBigEndian ? this.readF4be() : this.readF4le();
  }

  subStream(length: number): TapStreamReader {
    return TapStreamReader.fromArray(this.readBytes(length));
  }
}

export class TapStreamWriter extends KaitaiStreamWriter {
  constructor(
    lengthOrBuffer: number | StreamBufferType = 512,
    byteOffset?: number,
    isBigEndian = true,
  ) {
    if (typeof lengthOrBuffer === 'number') {
      lengthOrBuffer = new ArrayBuffer(lengthOrBuffer);
    }
    super(lengthOrBuffer, byteOffset, isBigEndian);
  }

  public static create(size: number) {
    return new TapStreamWriter(new ArrayBuffer(size));
  }

  writeFunction(type: string, options: any, fieldValue?: any, fieldSize?: any) {
    switch (type) {
      case 'crc32':
        return this.addCRC(options);
      case 'padding':
        if (fieldValue) {
          debug('Padding is already set');
          return this.writeBytes(fieldValue);
        } else {
          if (typeof fieldSize == undefined) {
            throw new Error(
              `Padding function expect padding size as a second argument. Args: ${JSON.stringify(
                arguments,
              )}`,
            );
          }
          return this.addPadding(fieldSize);
        }
      default:
        throw new Error(`Invalid writer function type: ${type}`);
    }
  }

  addPadding(padSize: number): this {
    this.writeBytes(new Uint8Array(padSize));
    return this;
  }

  addCRC(options?: { offset?: number }): this {
    let buffer = this.toBytes;
    if (options && options.offset) {
      buffer = buffer.slice(options.offset);
    }
    const crc = CRC.fromBytes(buffer);
    this.writeU4(crc);
    // console.info(`TODO REMOVE Compute CRC from ${options ? options.offset : 0}:${buffer.length} 0x${bufferToHexString(buffer)} => 0x${crc.toString(16)}`)
    return this;
  }

  writeUnsigned(value: number, length: number): this {
    this.writeU(value, length);
    return this;
  }

  writeU4(value: number): this {
    this.writeU(value, 4);
    return this;
  }

  writeU2(value: number): this {
    this.writeU(value, 2);
    return this;
  }

  writeU1(value: number): this {
    this.writeU(value, 1);
    return this;
  }

  writeF4(value: number): this {
    this.writeF(value, 4);
    return this;
  }

  writeFloat(value: number, length: number): this {
    this.writeF(value, length);
    return this;
  }

  writeSigned(value: number, length: number): this {
    this.writeSigned(value, length);
    return this;
  }

  writeString(value: string | undefined, length: number = value?.length || 0) {
    return this.writeStr(value || '', length);
  }

  writeStr(value: string, length: number = value.length): this {
    let i = 1;
    const stringWriteLength = Math.min(value.length, length);
    for (; i <= stringWriteLength; i++) {
      this.writeByte(value.charCodeAt(i - 1));
    }
    const remaining = length - stringWriteLength;
    if (remaining > 0) {
      this.writeBytes(new Uint8Array(remaining));
    }
    return this;
  }

  writeBitsInt(value: number, n: number): this {
    this.writeB(value, n);
    return this;
  }

  writeBits(value: number, n: number): this {
    this.writeB(value, n);
    return this;
  }

  writeBoolean(value: number | boolean, n: number): this {
    this.writeBits(value ? 1 : 0, n);
    return this;
  }

  // writeDouble(value: number, n: number): this {
  //     this.writeD(value, n);
  //     return this;
  // }
}
